import { useState } from 'react'
import { useIsMutating } from '@tanstack/react-query'
import { usePrimaryImage, type ResolvedImages, type ResolvedPhoto } from '@/api/produktbilder'
import { Button } from './ui/button'

const reasons = { no_source: 'Noch keine Bilder', pending_ingest: 'Archivierung ausstehend', file_missing: 'Bild lokal nicht verfügbar' }
/** Static nginx media URL: image bytes never traverse authenticated/rate-limited API routes. */
export function LocalProductPhoto({ photo, alt, className = '' }: { photo: ResolvedPhoto; alt: string; className?: string }) {
  const [failed, setFailed] = useState<string | null>(null)
  return <div data-testid="product-photo" data-file-hash={photo.fileHash} className={`grid min-w-0 place-items-center ${className}`}>
    {!photo.publicUrl || failed === photo.publicUrl ? <span className="text-xs text-ink-2">{reasons.file_missing}</span>
      : <img src={photo.publicUrl} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-contain" onError={() => setFailed(photo.publicUrl)} />}
  </div>
}
export function ProductPhoto({ images, alt, compact = false }: { images?: ResolvedImages; alt: string; compact?: boolean }) {
  if (images?.primary) return <LocalProductPhoto photo={images.primary} alt={alt} className={compact ? 'size-12' : 'h-full w-full'} />
  if (images?.components.length) return <div className="min-w-0 space-y-1" aria-label={`Komponentenbilder: ${alt}`}>
    <span className="block text-[10px] text-ink-2">Komponentenbilder</span>
    <div className="flex flex-wrap gap-1">{images.components.map((c, index) => <div key={`${c.sku}-${index}`} className={compact ? 'w-14' : 'w-24'}>
      <span className="block break-all text-[10px] text-ink-2">{c.quantity} × {c.sku}</span>
      <div className={compact ? 'min-h-10' : 'h-20'}><ProductPhoto images={c} alt={`${c.sku}: ${alt}`} compact={compact} /></div>
    </div>)}</div>
  </div>
  return <span className="text-xs text-ink-2">{images ? reasons[images.reason ?? 'no_source'] : 'Bild wird geladen…'}</span>
}
export function PrimaryImageButton({ sku, photo, usageId, isManualPrimary = photo?.match === 'manual' && photo.sourceSku === sku }: { sku: string; photo: ResolvedPhoto | null; usageId?: number; isManualPrimary?: boolean }) {
  const selection = usePrimaryImage(sku)
  const busy = useIsMutating({ mutationKey: ['primary-image', sku] }) > 0
  return <div className="min-w-0">
    <Button type="button" size="sm" variant="outline" disabled={isManualPrimary || busy || (!photo && usageId == null)} onClick={() => selection.mutate({ photo, usageId })}>{isManualPrimary ? 'Hauptbild ✓' : 'Als Hauptbild'}</Button>
    {selection.error && <p role="alert" className="mt-1 break-words text-xs text-coral">{selection.error.message}</p>}
  </div>
}
